<template>
    <order-summary
        :order="order"
        :summary="summary"
        class="position-relative">
        <template #content="{ orderDetails }">
            <div class="tile-title position-relative align-items-start">
                <div>
                    <h2>Order Details</h2>
                    <p class="text-muted no-margin" v-fs-block>Placed on {{ orderDetails.order.startTime | dateTime
                        }}</p>
                </div>
                <r-button type="link" class="no-flex" @click="orderCustomFieldModal = true">Edit</r-button>
            </div>
            <div class="tile-content">
                <v-inline-loader v-if="isLoading || isUpdatingOrder"/>
                <p class="text-muted stack-m" v-fs-block v-if="orderDetails.order && orderDetails.order.id">
                    <router-link :to="{name: 'order-details', params: {id: orderDetails.order.id}}">
                        {{orderDetails.order.id}}
                    </router-link>
                </p>
                <div class="stack-m" v-fs-block>
                    <r-badge
                        class="inline-s"
                        :type="status.type"
                        v-for="(status, statusIndex) in orderDetails.statusBadges"
                        :key="statusIndex">{{status.value | capitalize}}
                    </r-badge>
                </div>
                <div class="position-relative" v-fs-block>
                    <div v-if="!orderDetails.summary">
                        <v-inline-loader/>
                    </div>
                    <div v-else>
                        <table
                            class="table table-definition table-collapsed table-compact">
                            <tr v-for="(metric, index) in orderDetails.summaryMetrics" :key="index">
                                <td>{{metric.label}}</td>
                                <td>{{metric.value}}</td>
                            </tr>
                            <tr>
                                <td>Autopay</td>
                                <td>{{orderDetails.order.autopay ? 'Yes' : 'No'}}</td>
                            </tr>
                            <tr v-if="invoiceTimeShift">
                                <td style="vertical-align: top">Invoice time shift</td>
                                <td>
                                    <invoice-time-shift-preview :model="invoiceTimeShift"/>
                                </td>
                            </tr>
                            <tr v-if="periodAnchor">
                                <td style="vertical-align: top">Service anchor</td>
                                <td>
                                    <recurring-interval-preview :model="periodAnchor"/>
                                </td>
                            </tr>
                            <tr v-for="(field, index) in customFields" :key="`custom${index}`">
                                <td>{{field.name}}</td>
                                <td>{{field.value}}</td>
                            </tr>
                        </table>
                    </div>
                    <div class="margin-top-l">
                        <div class="grid-content">
                            <div class="grid-item grid-item-full" v-if="orderDetails.canCancelAndUpdateRenewal && !order.isPending">
                                <cancel-reactivate-order :order="orderDetails.order"/>
                            </div>
                            <template v-if="!orderDetails.order.isCanceled">
                                <div class="grid-item grid-item-full">
                                    <redeem-coupon :order="orderDetails.order" :customer="customer"/>
                                </div>
                                <div v-if="orderDetails.canCancelAndUpdateRenewal" class="grid-item grid-item-full">
                                    <update-renewal :order="orderDetails.order"/>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
            <v-modal v-show="orderCustomFieldModal" title="Edit Order" @close="orderCustomFieldModal = false">
                <div slot="content">
                    <r-checkbox
                        class="stack-m"
                        label="Autopay"
                        :value="true"
                        v-model="orderUpdate.autopay"
                        :disabled="orderUpdate.isCanceled"
                    />
                    <div class="grid-content">
                        <v-custom-fields-manager
                            fluid
                            resource-type="subscriptions"
                            v-model="orderUpdate.customFields"/>
                    </div>
                </div>
                <template slot="actions">
                    <r-button type="primary" @click="saveCustomFields">Save</r-button>
                </template>
            </v-modal>
        </template>
    </order-summary>
</template>

<script>
    import vModal from '@/components/modal/v-modal';
    import vInlineLoader from '@/components/v-inline-loader';
    import vCustomFieldsManager from '@/components/custom-fields/v-custom-fields-manager';
    import OrderSummary from '@/modules/orders/components/order-summary';
    import RedeemCoupon from '@/modules/orders/components/order-summary/redeem-coupon';
    import UpdateRenewal from '@/modules/orders/components/order-summary/update-renewal';
    import CancelReactivateOrder from '@/modules/orders/components/order-summary/cancel-reactivate-order';
    import OrderSummaryActions from '@/modules/mixins/order-summary/order-summary-actions';
    import CustomFieldsValuesMixin from '@/mixins/custom-fields-values-mixin';
    import OrderModel from '@/common/models/order-model';
    import InvoiceTimeShiftPreview from '@/components/billing-schedule/invoice-time-shift-preview';
    import RecurringIntervalPreview from '@/components/billing-schedule/recurring-interval-preview';

    export default {
        components: {
            vModal,
            vInlineLoader,
            OrderSummary,
            vCustomFieldsManager,
            RedeemCoupon,
            UpdateRenewal,
            CancelReactivateOrder,
            InvoiceTimeShiftPreview,
            RecurringIntervalPreview,
        },
        mixins: [OrderSummaryActions, CustomFieldsValuesMixin],
        props: {
            order: {
                type: Object,
                required: true,
            },
            customer: {
                type: Object,
                required: true,
            },
            summary: {
                type: [Array, Object],
                default: [],
            },
            ordersCustomFieldsSchema: {
                type: Array,
            },
            isLoading: {
                type: Boolean,
                default: false,
            },
        },
        watch: {
            order: {
                handler(value) {
                    this.orderUpdate = new OrderModel(value);
                },
                deep: true,
            },
        },
        data() {
            return {
                isUpdatingOrder: false,
                orderCustomFieldModal: false,
                orderUpdate: new OrderModel(),
            };
        },
        computed: {
            customFields() {
                return this.getCustomFields('order', this.getCustomFieldsTypes(this.ordersCustomFieldsSchema));
            },
            invoiceTimeShift() {
                return this.order.invoiceTimeShift;
            },
            periodAnchor() {
                return this.order.recurringInterval;
            },
        },
        methods: {
            saveCustomFields() {
                this.orderCustomFieldModal = false;
                this.updateOrder({
                    autopay: this.orderUpdate.autopay,
                    customFields: this.orderUpdate.customFields,
                });
            },
        },
    };
</script>
